import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Users, Building2, FolderOpen, Package, Bot, AlertTriangle,
  ArrowRight, Clock, ScrollText,
} from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { AdminShell } from './admin-shell';

interface DashboardData {
  counts: {
    usersTotal: number;
    usersSuspended: number;
    orgsPersonal: number;
    orgsTeam: number;
    orgsArchived: number;
    portfoliosTotal: number;
    projectsTotal: number;
    runsTotal: number;
    runsRunning: number;
    runsQueued: number;
    findingsTotal: number;
    findingsCritical: number;
    findingsHigh: number;
    findingsMedium: number;
    findingsLow: number;
    cost30d: number;
    costAllTime: number;
  };
  recentRuns: Array<{
    runId: string; orgId: string; projectId: string; userId: string;
    agentType: string; status: string; createdAt: string;
    orgName: string | null; projectName: string | null; userEmail: string | null;
  }>;
  recentOrgs: Array<{
    orgId: string; name: string; kind: string; createdAt: string;
    creatorEmail: string | null;
  }>;
  recentUsers: Array<{
    userId: string; email: string; displayName: string | null;
    plan: string | null; suspendedAt: string | null; createdAt: string;
  }>;
  recentAudit: Array<{
    auditId: string; actorEmail: string | null; action: string;
    targetType: string; targetId: string; summary: string | null; createdAt: string;
  }>;
}

function ago(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function AdminDashboardPage() {
  const { data } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => apiClient.adminDashboard(),
    refetchInterval: 30_000,
  });

  const d = data?.data as DashboardData | undefined;
  const c = d?.counts;

  return (
    <AdminShell>
      {/* Big counts row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatTile label="Users" value={c?.usersTotal} sub={c?.usersSuspended ? `${c.usersSuspended} suspended` : null} icon={Users} tone="blue" />
        <StatTile label="Orgs" value={(c?.orgsPersonal ?? 0) + (c?.orgsTeam ?? 0)} sub={c ? `${c.orgsTeam} team · ${c.orgsPersonal} personal` : null} icon={Building2} tone="indigo" />
        <StatTile label="Portfolios" value={c?.portfoliosTotal} icon={FolderOpen} tone="cyan" />
        <StatTile label="Projects" value={c?.projectsTotal} icon={Package} tone="emerald" />
        <StatTile label="Agent Runs" value={c?.runsTotal} sub={c ? `${c.runsRunning} running · ${c.runsQueued} queued` : null} icon={Bot} tone="amber" />
        <StatTile label="Findings" value={c?.findingsTotal} sub={c ? `${c.findingsCritical} critical` : null} icon={AlertTriangle} tone="red" />
      </div>

      {/* Findings severity breakdown */}
      <Card className="p-6">
        <h2 className="text-base font-bold text-foreground uppercase tracking-wide mb-4">Open findings by severity</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SevTile label="Critical" value={c?.findingsCritical} cls="bg-red-500/15 text-red-600 dark:text-red-400" />
          <SevTile label="High" value={c?.findingsHigh} cls="bg-orange-500/15 text-orange-600 dark:text-orange-400" />
          <SevTile label="Medium" value={c?.findingsMedium} cls="bg-yellow-500/15 text-yellow-600 dark:text-yellow-500" />
          <SevTile label="Low" value={c?.findingsLow} cls="bg-blue-500/15 text-blue-600 dark:text-blue-400" />
        </div>
      </Card>

      {/* LLM cost rollup */}
      <Card className="p-6">
        <h2 className="text-base font-bold text-foreground uppercase tracking-wide mb-4">LLM cost</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg p-4 bg-saffron/10 text-saffron">
            <p className="text-2xl font-bold tabular-nums">${(c?.cost30d ?? 0).toFixed(2)}</p>
            <p className="text-xs uppercase tracking-wider opacity-80">Last 30 days</p>
          </div>
          <div className="rounded-lg p-4 bg-muted text-muted-foreground">
            <p className="text-2xl font-bold tabular-nums">${(c?.costAllTime ?? 0).toFixed(2)}</p>
            <p className="text-xs uppercase tracking-wider opacity-80">All time</p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent agent runs */}
        <Card className="p-0 overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <p className="text-sm font-medium uppercase tracking-wide">Recent agent runs</p>
          </div>
          <div className="divide-y divide-border">
            {d?.recentRuns?.length ? d.recentRuns.map((r) => (
              <div key={r.runId} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {r.agentType} · <span className="text-muted-foreground">{r.projectName ?? r.projectId}</span>
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {r.orgName ?? r.orgId} · {r.userEmail ?? r.userId}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0 text-xs">
                  <StatusPill status={r.status} />
                  <span className="text-muted-foreground"><Clock className="h-3 w-3 inline mr-1" />{ago(r.createdAt)}</span>
                </div>
              </div>
            )) : <p className="px-5 py-6 text-sm text-muted-foreground text-center">No agent runs yet.</p>}
          </div>
        </Card>

        {/* Recent orgs + users + audit */}
        <div className="space-y-4">
          <Card className="p-0 overflow-hidden">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <p className="text-sm font-medium uppercase tracking-wide">Recent orgs</p>
              <Link to="/admin/orgs" className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 inline-flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link>
            </div>
            <div className="divide-y divide-border">
              {d?.recentOrgs?.length ? d.recentOrgs.map((o) => (
                <Link key={o.orgId} to={`/admin/orgs/${o.orgId}`} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-muted/40 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{o.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{o.kind} · created by {o.creatorEmail ?? '—'}</p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{ago(o.createdAt)}</span>
                </Link>
              )) : <p className="px-5 py-6 text-sm text-muted-foreground text-center">No orgs yet.</p>}
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <p className="text-sm font-medium uppercase tracking-wide">Recent signups</p>
              <Link to="/admin/users" className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 inline-flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link>
            </div>
            <div className="divide-y divide-border">
              {d?.recentUsers?.length ? d.recentUsers.map((u) => (
                <Link key={u.userId} to={`/admin/users/${u.userId}`} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-muted/40 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.displayName ?? u.email}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{ago(u.createdAt)}</span>
                </Link>
              )) : <p className="px-5 py-6 text-sm text-muted-foreground text-center">No users yet.</p>}
            </div>
          </Card>
        </div>
      </div>

      {/* Audit log tail */}
      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <p className="text-sm font-medium uppercase tracking-wide flex items-center gap-2">
            <ScrollText className="h-4 w-4" /> Recent admin activity
          </p>
          <Link to="/admin/audit-logs" className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 inline-flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link>
        </div>
        <div className="divide-y divide-border">
          {d?.recentAudit?.length ? d.recentAudit.map((a) => (
            <div key={a.auditId} className="px-5 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm">
                  <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground mr-2">{a.action}</span>
                  {a.summary ?? `${a.targetType}/${a.targetId}`}
                </p>
                <p className="text-xs text-muted-foreground">by {a.actorEmail ?? '—'}</p>
              </div>
              <span className="text-xs text-muted-foreground shrink-0">{ago(a.createdAt)}</span>
            </div>
          )) : <p className="px-5 py-6 text-sm text-muted-foreground text-center">No admin actions yet.</p>}
        </div>
      </Card>
    </AdminShell>
  );
}

const TONE: Record<string, string> = {
  blue: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  indigo: 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400',
  cyan: 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-400',
  emerald: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  amber: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  red: 'bg-red-500/15 text-red-600 dark:text-red-400',
};

function StatTile({ label, value, sub, icon: Icon, tone }: {
  label: string; value: number | undefined; sub?: string | null;
  icon: React.ComponentType<{ className?: string }>; tone: keyof typeof TONE;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div className={cn('h-9 w-9 rounded-lg flex items-center justify-center', TONE[tone])}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="text-2xl font-bold text-foreground mt-2 tabular-nums">{value ?? '—'}</p>
      <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-1 truncate">{sub}</p>}
    </Card>
  );
}

function SevTile({ label, value, cls }: { label: string; value: number | undefined; cls: string }) {
  return (
    <div className={cn('rounded-lg p-4', cls)}>
      <p className="text-2xl font-bold tabular-nums">{value ?? 0}</p>
      <p className="text-xs uppercase tracking-wider opacity-80">{label}</p>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'completed' ? 'bg-green-500/15 text-green-700 dark:text-green-400'
    : status === 'running' ? 'bg-blue-500/15 text-blue-700 dark:text-blue-400'
    : status === 'queued' ? 'bg-muted text-muted-foreground'
    : status === 'failed' ? 'bg-red-500/15 text-red-700 dark:text-red-400'
    : 'bg-muted text-muted-foreground';
  return <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide', tone)}>{status}</span>;
}
